import { useEffect, useRef } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { api, clearSessionToken } from "@/lib/api"
import type { User } from "@/types"

export const AUTH_SESSION_QUERY_KEY = ["auth", "session"] as const

export const AUTH_SESSION_STALE_MS = 5 * 60 * 1000

type SessionResponse = { user: User | null }

export function getAuthSessionQueryOptions() {
  return {
    queryKey: AUTH_SESSION_QUERY_KEY,
    queryFn: () => api.get<SessionResponse>("/auth/session"),
    retry: false as const,
    staleTime: AUTH_SESSION_STALE_MS,
    throwOnError: false as const,
    refetchOnWindowFocus: true as const,
  }
}

export type UseAuthSessionResult = {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  /** @deprecated Use `user` */
  currentUser: User | null
  /** @deprecated Use `isLoading` */
  sessionLoading: boolean
  /** Full session API response */
  sessionData: SessionResponse | undefined
}

/**
 * TanStack Query hook for the current auth session (GET /auth/session).
 */
export function useAuthSession(): UseAuthSessionResult {
  const queryClient = useQueryClient()
  const wasAuthenticated = useRef(false)

  const query = useQuery(getAuthSessionQueryOptions())

  const user = query.data?.user ?? null
  const isAuthenticated = !!user
  const isLoading = query.isLoading

  useEffect(() => {
    if (isAuthenticated) {
      wasAuthenticated.current = true
    } else if (wasAuthenticated.current && !isLoading) {
      wasAuthenticated.current = false
      clearSessionToken()
      queryClient.clear()
    }
  }, [isAuthenticated, isLoading, queryClient])

  return {
    user,
    isAuthenticated,
    isLoading,
    currentUser: user,
    sessionLoading: isLoading,
    sessionData: query.data,
  }
}
